import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { API } from "../lib/api";

export default function EnrollForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/enroll/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // Per design, always show success — don't leak account existence
      setSent(true);
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#FAF7EE] min-h-screen" data-testid="forgot-password-page">
      <div className="border-b border-[#0F2A1F]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/enroll" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[#0F2A1F] text-white flex items-center justify-center font-bold text-sm">DH</div>
            <div className="leading-tight">
              <div className="font-serif text-lg text-[#0F2A1F]">DirectHire</div>
              <div className="text-[10px] tracking-[0.25em] text-[#0F2A1F]/55 uppercase">Enrollment</div>
            </div>
          </Link>
          <Link to="/enroll/login" className="inline-flex items-center gap-1.5 text-sm text-[#0F2A1F]/80 hover:text-[#0F2A1F]" data-testid="back-to-signin">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </div>
      </div>
      <section className="py-16 md:py-24">
        <div className="max-w-md mx-auto px-6">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Reset password</div>
          <h1 className="mt-3 text-4xl md:text-5xl font-bold text-[#0F2A1F] leading-tight">Forgot your password?</h1>
          <p className="mt-3 text-[#0F2A1F]/65">Enter your email and we'll send you a secure reset link, valid for 30 minutes.</p>

          {sent ? (
            <div className="mt-10 rounded-xl bg-white p-8 ring-1 ring-[#0F2A1F]/10 text-center" data-testid="forgot-success-card">
              <div className="text-xl font-bold text-[#0F2A1F]">Check your email</div>
              <p className="mt-3 text-sm text-[#0F2A1F]/65">If an account exists for <strong>{email}</strong>, a reset link is on the way. Be sure to check spam.</p>
              <Link to="/enroll/login" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#0F2A1F] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#163b2c]" data-testid="forgot-back-signin">
                Back to sign in <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 rounded-xl bg-white p-8 ring-1 ring-[#0F2A1F]/10 space-y-5" data-testid="forgot-form">
              <div>
                <Label htmlFor="femail" className="text-[#0F2A1F]">Email</Label>
                <Input id="femail" type="email" data-testid="forgot-email" className="mt-2 bg-[#FAF7EE] border-[#0F2A1F]/15 focus-visible:ring-[#0F2A1F]" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <button type="submit" disabled={busy} className="w-full rounded-md bg-[#0F2A1F] text-white py-3 text-sm font-medium hover:bg-[#163b2c] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="forgot-submit-btn">
                {busy ? "Sending…" : (<>Send reset link <ArrowRight className="h-4 w-4" /></>)}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
